// 자동 이동용 길찾기(순수 로직, DOM·three 없음). 맵 격자(1m 칸)에서 영웅 반지름만큼 비어 있는 칸만 지나는 최단 경로를 찾고,
// 직선으로 갈 수 있는 구간은 줄여서 꺾는 점만 남긴다. 계획은 시작·막힘 때만 하고, 배열은 처음 한 번만 만든다.
import { Cell, cellAt, gateAt, type MapGrid } from './levels';

export const NAV_MAX_POINTS = 64;

export interface NavWorld {
  readonly grid: MapGrid;
  /** map.gates 순서의 열림 정도(0~1). 0.99 미만이면 막힌 것으로 본다 */
  readonly gateOpen: ArrayLike<number>;
  readonly radius: number;
  /** 격자 밖의 추가 장애물(마을 NPC 몸체 등) */
  readonly blocked?: (x: number, z: number) => boolean;
}

export class NavPlanner {
  readonly xs = new Float32Array(NAV_MAX_POINTS);
  readonly zs = new Float32Array(NAV_MAX_POINTS);
  count = 0;
  private parent = new Int32Array(0);
  private queue = new Int32Array(0);
  private cells = new Int32Array(0);

  /** 반지름 원의 네 모서리가 모두 바닥이고 닫힌 문·장애물이 아니면 true */
  walkable(w: NavWorld, x: number, z: number): boolean {
    const r = w.radius;
    for (let i = 0; i < 4; i++) {
      const px = x + (i & 1 ? r : -r), pz = z + (i & 2 ? r : -r);
      if (cellAt(w.grid, px, pz) !== Cell.Floor) return false;
      const g = gateAt(w.grid, px, pz);
      if (g >= 0 && !((w.gateOpen[g] ?? 0) >= 0.99)) return false;
    }
    return !(w.blocked && w.blocked(x, z));
  }

  /** 두 점 사이를 0.25m 간격으로 확인해 끝까지 걸을 수 있으면 true */
  clear(w: NavWorld, ax: number, az: number, bx: number, bz: number): boolean {
    const d = Math.hypot(bx - ax, bz - az), n = Math.max(1, Math.ceil(d / 0.25));
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      if (!this.walkable(w, ax + (bx - ax) * t, az + (bz - az) * t)) return false;
    }
    return true;
  }

  /**
   * (sx,sz)에서 goal(x,z)이 참인 칸까지 경로를 찾아 xs/zs에 꺾는 점을 쓴다(시작점 제외, 마지막 = 도착 칸 중심).
   * 찾지 못하면 false. 넓이 우선 탐색(8방향, 모서리 끼어 지나가기 금지).
   */
  plan(w: NavWorld, sx: number, sz: number, goal: (x: number, z: number) => boolean): boolean {
    const g = w.grid, W = g.width, H = g.height, n = W * H;
    if (this.parent.length !== n) { this.parent = new Int32Array(n); this.queue = new Int32Array(n); this.cells = new Int32Array(n); }
    const parent = this.parent, queue = this.queue;
    parent.fill(-2);
    const cx = Math.floor(sx - g.originX), cz = Math.floor(sz - g.originZ);
    this.count = 0;
    if (cx < 0 || cz < 0 || cx >= W || cz >= H) return false;
    const start = cz * W + cx;
    parent[start] = -1; queue[0] = start;
    const ox = g.originX + 0.5, oz = g.originZ + 0.5;
    let tail = 1, found = -1;
    for (let head = 0; head < tail; head++) {
      const c = queue[head], x = c % W, z = (c - x) / W;
      if (c !== start && goal(ox + x, oz + z)) { found = c; break; }
      for (let k = 0; k < 8; k++) {
        const dx = k < 4 ? (k === 0 ? 1 : k === 1 ? -1 : 0) : (k & 1 ? 1 : -1);
        const dz = k < 4 ? (k === 2 ? 1 : k === 3 ? -1 : 0) : (k < 6 ? 1 : -1);
        const nx = x + dx, nz = z + dz;
        if (nx < 0 || nz < 0 || nx >= W || nz >= H) continue;
        const nc = nz * W + nx;
        if (parent[nc] !== -2 || !this.walkable(w, ox + nx, oz + nz)) continue;
        if (dx !== 0 && dz !== 0 && (!this.walkable(w, ox + nx, oz + z) || !this.walkable(w, ox + x, oz + nz))) continue;
        parent[nc] = c; queue[tail++] = nc;
      }
    }
    if (found < 0) return false;
    // 칸 경로(도착 → 시작)를 시작 → 도착 순서로
    let len = 0;
    for (let c = found; c !== -1; c = parent[c]) this.cells[len++] = c;
    // 줄이기: 지금 점에서 직선으로 닿는 가장 먼 칸까지 건너뛴다
    let ax = sx, az = sz, i = len - 2;
    while (i >= 0 && this.count < NAV_MAX_POINTS) {
      let j = i;
      for (let k = 0; k <= i; k++) {
        const c = this.cells[k];
        if (this.clear(w, ax, az, ox + (c % W), oz + Math.floor(c / W))) { j = k; break; }
      }
      const c = this.cells[j];
      ax = ox + (c % W); az = oz + Math.floor(c / W);
      this.xs[this.count] = ax; this.zs[this.count] = az; this.count++;
      i = j - 1;
    }
    return this.count > 0;
  }
}

